import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Label, useToast } from '@caruso/ui';
import { usersService, UserRole, CreateUserDto, UpdateUserDto } from '@/services/users.service';
import { userGroupsService } from '@/services/user-groups.service';
import { ArrowLeft, Save, Shield, UserCog, Calendar, Users, MessageSquare } from 'lucide-react';
import { cn } from '@caruso/ui';

export function UserFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: UserRole.USER,
    hasCalendar: false,
    canSeeAllConversations: false,
    userGroupId: null as string | null,
  });

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersService.getById(id!),
    enabled: isEditing,
  });

  const { data: userGroups = [] } = useQuery({
    queryKey: ['user-groups'],
    queryFn: userGroupsService.getAll,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '', // Não preencher senha ao editar
        role: user.role,
        hasCalendar: user.hasCalendar || false,
        canSeeAllConversations: user.canSeeAllConversations || false,
        userGroupId: user.userGroupId || null,
      });
    }
  }, [user]);

  const createMutation = useMutation({
    mutationFn: (data: CreateUserDto) => usersService.create(data),
    onSuccess: () => {
      toast({
        title: 'Usuário criado',
        description: 'Usuário criado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/app/usuarios');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar usuário',
        description: error.response?.data?.message || 'Não foi possível criar o usuário',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserDto }) =>
      usersService.update(id, data),
    onSuccess: () => {
      toast({
        title: 'Usuário atualizado',
        description: 'Usuário atualizado com sucesso',
        variant: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      navigate('/app/usuarios');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar usuário',
        description: error.response?.data?.message || 'Não foi possível atualizar o usuário',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: 'Nome obrigatório',
        description: 'Por favor, informe o nome do usuário',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.email.trim()) {
      toast({
        title: 'Email obrigatório',
        description: 'Por favor, informe o email do usuário',
        variant: 'destructive',
      });
      return;
    }

    if (!isEditing && !formData.password.trim()) {
      toast({
        title: 'Senha obrigatória',
        description: 'Por favor, informe a senha do usuário',
        variant: 'destructive',
      });
      return;
    }

    if (isEditing) {
      const updateData: UpdateUserDto = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        hasCalendar: formData.hasCalendar,
        canSeeAllConversations: formData.canSeeAllConversations,
        userGroupId: formData.userGroupId,
      };

      // Só incluir senha se foi preenchida
      if (formData.password.trim()) {
        updateData.password = formData.password;
      }

      updateMutation.mutate({ id: id!, data: updateData });
    } else {
      const createData: CreateUserDto = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        hasCalendar: formData.hasCalendar,
        canSeeAllConversations: formData.canSeeAllConversations,
        userGroupId: formData.userGroupId,
      };

      createMutation.mutate(createData);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEditing && isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-brand-smoke rounded w-48"></div>
          <div className="h-96 bg-brand-smoke rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/app/usuarios')}
          className="text-brand-gold hover:bg-brand-gold/10"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-4xl font-bold text-white">
            {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
          </h1>
          <p className="text-brand-ivory/80 mt-2 text-lg">
            {isEditing
              ? 'Atualize as informações do usuário'
              : 'Preencha os dados para criar um novo usuário'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="p-8 bg-brand-smoke border-brand-gold/20">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white font-semibold">
                  Nome Completo *
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: João da Silva"
                  className="text-base"
                  required
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white font-semibold">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Ex: joao@email.com"
                  className="text-base"
                  required
                />
              </div>
            </div>

            {/* Senha */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white font-semibold">
                Senha {isEditing ? '(deixe em branco para não alterar)' : '*'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={isEditing ? 'Digite apenas se quiser alterar a senha' : 'Digite uma senha segura'}
                className="text-base"
                required={!isEditing}
              />
              <p className="text-sm text-brand-ivory/60">
                {isEditing
                  ? 'A senha só será alterada se você preencher este campo'
                  : 'A senha deve ter no mínimo 6 caracteres'}
              </p>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label htmlFor="role" className="text-white font-semibold">
                Nível de Acesso *
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: UserRole.USER })}
                  className={cn(
                    'p-6 rounded-lg border-2 transition-all text-left',
                    formData.role === UserRole.USER
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-brand-gold/20 bg-brand-smoke/30 hover:border-blue-500/50',
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      'w-12 h-12 rounded-full flex items-center justify-center',
                      formData.role === UserRole.USER ? 'bg-blue-500/30' : 'bg-brand-smoke/50',
                    )}>
                      <UserCog className={cn(
                        'w-6 h-6',
                        formData.role === UserRole.USER ? 'text-blue-400' : 'text-brand-ivory/50',
                      )} />
                    </div>
                    <div className="flex-1">
                      <h3 className={cn(
                        'text-lg font-bold mb-1',
                        formData.role === UserRole.USER ? 'text-blue-400' : 'text-white',
                      )}>
                        Usuário
                      </h3>
                      <p className="text-sm text-brand-ivory/70">
                        Acesso limitado ao sistema. Pode visualizar e gerenciar dados básicos.
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: UserRole.ADMIN })}
                  className={cn(
                    'p-6 rounded-lg border-2 transition-all text-left',
                    formData.role === UserRole.ADMIN
                      ? 'border-brand-gold bg-brand-gold/20'
                      : 'border-brand-gold/20 bg-brand-smoke/30 hover:border-brand-gold/50',
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      'w-12 h-12 rounded-full flex items-center justify-center',
                      formData.role === UserRole.ADMIN ? 'bg-brand-gold/30' : 'bg-brand-smoke/50',
                    )}>
                      <Shield className={cn(
                        'w-6 h-6',
                        formData.role === UserRole.ADMIN ? 'text-brand-gold' : 'text-brand-ivory/50',
                      )} />
                    </div>
                    <div className="flex-1">
                      <h3 className={cn(
                        'text-lg font-bold mb-1',
                        formData.role === UserRole.ADMIN ? 'text-brand-gold' : 'text-white',
                      )}>
                        Administrador
                      </h3>
                      <p className="text-sm text-brand-ivory/70">
                        Acesso total ao sistema. Pode gerenciar usuários, configurações e todos os recursos.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Grupo de Usuário - Apenas para USER */}
            {formData.role === UserRole.USER && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-brand-gold" />
                  <Label htmlFor="userGroup" className="text-white font-semibold">
                    Grupo de Permissões
                  </Label>
                </div>
                <select
                  id="userGroup"
                  value={formData.userGroupId || ''}
                  onChange={(e) => setFormData({ ...formData, userGroupId: e.target.value || null })}
                  className="w-full px-4 py-3 bg-brand-smoke border border-brand-gold/30 rounded-lg text-white focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
                >
                  <option value="">Nenhum grupo (sem permissões personalizadas)</option>
                  {userGroups
                    .filter(group => group.active)
                    .map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name} {group.description ? `- ${group.description}` : ''}
                      </option>
                    ))}
                </select>
                <p className="text-sm text-brand-ivory/60">
                  O grupo define quais recursos do sistema este usuário pode acessar. Administradores têm acesso total automaticamente.
                </p>
              </div>
            )}

            {/* Agenda Habilitada */}
            <div className="space-y-2">
              <Label className="text-white font-semibold">Recursos Adicionais</Label>
              <div className="space-y-3">
                <label className="flex items-start gap-4 p-4 rounded-lg border-2 border-brand-gold/20 bg-brand-smoke/30 hover:border-purple-500/50 cursor-pointer transition-all">
                  <input
                    type="checkbox"
                    checked={formData.hasCalendar}
                    onChange={(e) => setFormData({ ...formData, hasCalendar: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-brand-gold/30 text-purple-500 focus:ring-purple-500 focus:ring-offset-0 bg-brand-smoke"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-5 h-5 text-purple-400" />
                      <span className="text-white font-semibold">Habilitar Agenda</span>
                    </div>
                    <p className="text-sm text-brand-ivory/70">
                      Permite que o usuário tenha acesso à funcionalidade de agenda/calendário e possa sincronizar com Google Calendar ou Apple Calendar
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-4 p-4 rounded-lg border-2 border-brand-gold/20 bg-brand-smoke/30 hover:border-green-500/50 cursor-pointer transition-all">
                  <input
                    type="checkbox"
                    checked={formData.canSeeAllConversations}
                    onChange={(e) => setFormData({ ...formData, canSeeAllConversations: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-brand-gold/30 text-green-500 focus:ring-green-500 focus:ring-offset-0 bg-brand-smoke"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <MessageSquare className="w-5 h-5 text-green-400" />
                      <span className="text-white font-semibold">Ver Todas as Conversas</span>
                    </div>
                    <p className="text-sm text-brand-ivory/70">
                      Permite que o usuário visualize todas as conversas do WebChat, independente do departamento ao qual pertence
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/app/usuarios')}
            disabled={isPending}
            className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90 font-semibold shadow-lg"
          >
            <Save className="w-5 h-5 mr-2" />
            {isPending ? 'Salvando...' : isEditing ? 'Atualizar Usuário' : 'Criar Usuário'}
          </Button>
        </div>
      </form>
    </div>
  );
}
